import { useCallback,useState } from 'react';

import { AsyncThunk } from '@reduxjs/toolkit';
import { useAppDispatch } from 'app/hooks';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

interface UseDeleteDialogHandlerOptions<TPayload, TResult> {
    /**
     * The async thunk action to dispatch for deletion
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deleteThunk: AsyncThunk<TResult, TPayload, any>;
    /**
     * Entity name for error messages (e.g., 'User', 'Role', 'Claim')
     */
    entityName: string;
    /**
     * Success message shown in snackbar
     */
    successMessage: string;
    /**
     * Called after successful deletion with the result
     */
    onSuccess?: (result: TResult) => void;
    /**
     * Custom error handler. If returns true, default error handling is skipped.
     */
    onError?: (error: { message: string }, statusCode: number | null) => boolean;
    /**
     * Whether to automatically close the dialog on success (default: true)
     */
    closeDialogOnSuccess?: boolean;
}

interface UseDeleteDialogHandlerReturn<TPayload> {
    /**
     * Whether a delete operation is in progress
     */
    processing: boolean;
    /**
     * Call this to trigger the delete operation
     */
    handleDelete: (payload: TPayload) => void;
}

/**
 * Hook to handle common delete dialog logic including:
 * - Processing state management
 * - Thunk dispatch and unwrap
 * - Snackbar notifications
 * - Error handling
 * - Dialog close
 */
export function useDeleteDialogHandler<TPayload, TResult = unknown>({
    deleteThunk,
    entityName,
    successMessage,
    onSuccess,
    onError,
    closeDialogOnSuccess = true,
}: UseDeleteDialogHandlerOptions<TPayload, TResult>): UseDeleteDialogHandlerReturn<TPayload> {
    const [processing, setProcessing] = useState(false);
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const handleDelete = useCallback((payload: TPayload) => {
        setProcessing(true);
        dispatch(deleteThunk(payload as TPayload & undefined))
            .unwrap()
            .then((result) => {
                if (closeDialogOnSuccess) {
                    dispatch(setDialogOpen(false));
                }
                enqueueSnackbar(successMessage, { variant: 'success' });
                onSuccess?.(result);
            })
            .catch((error: { message: string }) => {
                const statusCode = extractStatusCode(error.message);

                // Allow custom error handling to override default
                if (onError?.(error, statusCode)) {
                    return;
                }

                // Default error handling using existing utility
                handleDeleteDialogError(entityName, enqueueSnackbar, statusCode);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, [deleteThunk, entityName, successMessage, onSuccess, onError, closeDialogOnSuccess, dispatch, enqueueSnackbar]);

    return { processing, handleDelete };
}

/**
 * Creates a custom error handler that handles 409 conflict with a specific message.
 * Returns true if error was handled, false to delegate to default handler.
 */
export function createConflictErrorHandler(
    conflictMessage: string,
    enqueueSnackbar: (message: string, options: { variant: 'error' | 'success' | 'warning' | 'info' }) => void
) {
    return (error: { message: string }, statusCode: number | null): boolean => {
        if (statusCode === 409 && conflictMessage) {
            enqueueSnackbar(conflictMessage, { variant: 'error' });
            return true;
        }
        if (statusCode === 404) {
            // Silently ignore 404 (already deleted)
            return true;
        }
        // Delegate to default error handler
        return false;
    };
}
